export type ProjectStatus = 0 | 1 | 2 | 3 | 4 | 5;
export type ProjectPriority = 0 | 1 | 2 | 3;

export const PROJECT_STATUS_LABELS: Record<number, string> = {
  0: 'Planning',
  1: 'Active',
  2: 'On Hold',
  3: 'Completed',
  4: 'Cancelled',
  5: 'Archived'
};

export const PROJECT_PRIORITY_LABELS: Record<number, string> = {
  0: 'Low',
  1: 'Medium',
  2: 'High',
  3: 'Critical'
};

export const PROJECT_STATUS_COLORS: Record<number, string> = {
  0: 'primary',
  1: 'accent',
  2: 'warn',
  3: '',
  4: '',
  5: ''
};

export const PROJECT_MEMBER_ROLES = [
  'Member', 'Developer', 'Designer', 'QA', 'TeamLead', 'ProjectManager', 'ASO'
];

export const PLATFORMS = ['Android', 'iOS', 'Both', 'Web', 'Desktop'];

/** Common project categories for agile portfolio grouping. */
export const PROJECT_CATEGORIES = [
  'Mobile', 'Web', 'Desktop', 'API', 'Internal', 'DevOps', 'Research', 'Other'
];

export interface ProjectActivityDto {
  id: string;
  activityType: number;
  activityTypeLabel: string;
  message: string;
  actorUserId?: string;
  actorName?: string;
  occurredAt: string;
}

export interface ProjectDto {
  id: string;
  name: string;
  code?: string;
  issueKeyPrefix?: string;
  category?: string;
  description?: string;
  status: number;
  statusLabel: string;
  priority: number;
  priorityLabel: string;
  accountId?: string;
  accountName?: string;
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  ownerId?: string;
  ownerName?: string;
  departmentId?: string;
  progress: number;
  budget?: number;
  color?: string;
  isActive: boolean;
  isPublishedApp: boolean;
  platform?: string;
  storeUrl?: string;
  packageName?: string;
  memberCount: number;
  taskCount: number;
  openTaskCount: number;
  isFavorite?: boolean;
  archivedAt?: string;
  isTemplate?: boolean;
  templateProjectId?: string;
  statusBeforeArchive?: number | null;
  healthLabel?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface ProjectMemberDto {
  id: string;
  employeeId: string;
  employeeName: string;
  employeeEmail?: string;
  jobTitle?: string;
  role: string;
  isActive: boolean;
  joinedAt: string;
}

export interface ProjectTasksSummaryDto {
  total: number;
  open: number;
  inProgress: number;
  done: number;
}

export interface ProjectDetailDto extends ProjectDto {
  members: ProjectMemberDto[];
  tasksSummary: ProjectTasksSummaryDto;
  issueWorkflowId?: string;
  issueWorkflowName?: string;
}

export interface CreateProjectRequest {
  name: string;
  code?: string;
  issueKeyPrefix?: string;
  category?: string;
  description?: string;
  priority: number;
  accountId?: string;
  departmentId?: string;
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  ownerId?: string;
  budget?: number;
  color?: string;
  isActive?: boolean;
  isPublishedApp?: boolean;
  platform?: string;
  storeUrl?: string;
  packageName?: string;
  isTemplate?: boolean;
}

export interface UpdateProjectRequest extends CreateProjectRequest {
  status: number;
  progress: number;
}

export interface DuplicateProjectRequest {
  name?: string;
  code?: string;
  issueKeyPrefix?: string;
  copyMembers?: boolean;
  copyComponents?: boolean;
  copyLabels?: boolean;
  copyBoards?: boolean;
  copyCustomFieldDefinitions?: boolean;
  asTemplate?: boolean;
}

export interface CreateFromTemplateRequest {
  name: string;
  code?: string;
  issueKeyPrefix?: string;
  ownerId?: string;
  accountId?: string;
  copyMembers?: boolean;
  copyComponents?: boolean;
  copyLabels?: boolean;
  copyBoards?: boolean;
  copyCustomFieldDefinitions?: boolean;
}

export interface AddMemberRequest {
  employeeId: string;
  role: string;
}

export interface UpdateMemberRequest {
  role: string;
  isActive: boolean;
}

export interface ProjectListParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: number;
  category?: string;
  accountId?: string;
  ownerId?: string;
  activeOnly?: boolean;
  publishedOnly?: boolean;
  favoritesOnly?: boolean;
  templatesOnly?: boolean;
  includeArchived?: boolean;
}
